import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, Trash2, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import NotificationService from '../../services/NotificationService';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const resolveNotificationLink = (rawLink) => {
        if (!rawLink) return null;
        const role = user?.role;
        const isStaff = ['admin', 'support', 'office_admin'].includes(role);
        const isAdmin  = role === 'admin' || role === 'office_admin';
        const isCollege = role === 'college_admin';

        // All valid frontend routes from App.js
        const VALID_ROUTES = new Set([
            '/dashboard', '/profile', '/settings', '/notifications',
            '/admin/dashboard', '/admin/student-onboarding', '/admin/add-recruiter',
            '/admin/recruiters', '/admin/email-tester', '/admin/notifications',
            '/admin/career-analytics', '/admin/proctor', '/admin/grievances',
            '/admin/community', '/admin/activity-log', '/admin/access-logs',
            '/college/dashboard', '/college/departments', '/college/degrees',
            '/college/students', '/college/placement',
            '/college/recruiter', '/college/career-analytics', '/college/courses',
            '/college/analytics', '/college/calendar', '/college/reports',
            '/college/notifications', '/college/student-progress',
            '/student/dashboard', '/student/courses', '/student/my-courses',
            '/student/passport', '/student/micro-assessments',
            '/recruiter/dashboard', '/recruiter/jobs', '/recruiter/applicants',
            '/colleges', '/colleges/onboard', '/manage-batches',
            '/users', '/users/create',
            '/courses', '/courses/create',
            '/assessments', '/assessments/create',
            '/analytics', '/analytics/dashboard',
            '/it-support', '/my-it-support',
            '/support/dashboard', '/support',
            '/case-log', '/security/sessions', '/institute-chain',
        ]);

        // Keyword → route mapping (checked in order, most specific first)
        const keywordRoutes = [
            // Support / Ticket
            { test: /support|ticket/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id) return isStaff ? `/support/${id}` : `/my-it-support`;
                return isStaff ? `/support/dashboard` : `/my-it-support`;
            }},
            // Career agent / analytics
            { test: /career/i, resolve: () =>
                isAdmin ? `/admin/career-analytics` : isCollege ? `/college/career-analytics` : `/dashboard`
            },
            // Community / post / announcement
            { test: /community|post|announcement/i, resolve: () =>
                isAdmin ? `/admin/community` : isCollege ? `/college/notifications` : `/notifications`
            },
            // Grievances
            { test: /grievance/i, resolve: () =>
                isAdmin ? `/admin/grievances` : `/notifications`
            },
            // Assessments / results
            { test: /assessment|result/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/assessments/view/${id}`;
                return `/notifications`;
            }},
            // Proctor
            { test: /proctor/i, resolve: () =>
                isAdmin ? `/admin/proctor` : `/notifications`
            },
            // Access / audit logs
            { test: /access.log|audit.log|activity.log/i, resolve: () =>
                isAdmin ? `/admin/access-logs` : `/notifications`
            },
            // Courses
            { test: /\/courses?\//i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/courses/${id}`;
                if (id && isCollege) return `/college/courses`;
                return `/notifications`;
            }},
            // Colleges / institutes
            { test: /college|institute/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/colleges/${id}`;
                return isCollege ? `/college/dashboard` : `/dashboard`;
            }},
            // Users / staff
            { test: /\/users?\//i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/users/${id}`;
                return isAdmin ? `/users` : `/dashboard`;
            }},
            // Students
            { test: /student/i, resolve: () =>
                isAdmin ? `/admin/student-onboarding` : isCollege ? `/college/students` : `/student/dashboard`
            },
        ];

        // 1. Try keyword-based routing first
        for (const { test, resolve } of keywordRoutes) {
            if (test.test(rawLink)) return resolve();
        }

        // 2. If the link itself is a known valid route, use it directly
        if (rawLink.startsWith('/')) {
            // Strip query params and hash for validation
            const basePath = rawLink.split('?')[0].split('#')[0];
            // Check exact match
            if (VALID_ROUTES.has(basePath)) return rawLink;
            // Check prefix match (for dynamic routes like /support/:id, /users/:id)
            const dynamicPrefixes = [
                '/support/', '/users/', '/colleges/', '/courses/', '/assessments/',
                '/college/students/', '/institute-chain/',
                '/student/courses/', '/student/micro-assessments/',
                '/admin/student-onboarding/', '/admin/edit-recruiter/',
            ];
            if (dynamicPrefixes.some(p => basePath.startsWith(p))) return rawLink;
        }

        // 3. Safe fallback — never 404
        return isAdmin ? `/admin/dashboard` : isCollege ? `/college/dashboard` : `/dashboard`;
    };

    const handleNotificationClick = async (n) => {
        if (!n.read) {
            await handleMarkRead(n._id);
        }
        setIsOpen(false);
        const resolved = resolveNotificationLink(n.link);
        if (resolved) navigate(resolved);
    };

    useEffect(() => {
        fetchNotifications();
        // Poll for new notifications every 2 minutes
        const interval = setInterval(fetchNotifications, 120000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchNotifications = async () => {
        try {
            const res = await NotificationService.getMyNotifications();
            if (res.success) {
                setNotifications(res.data);
                setUnreadCount(res.data.filter(n => !n.read).length);
            }
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            const res = await NotificationService.markAsRead(id);
            if (res.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Failed to mark notification as read', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const res = await NotificationService.markAsRead('all');
            if (res.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Failed to mark all as read', error);
        }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        try {
            const res = await NotificationService.deleteNotification(id);
            if (res.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Failed to delete notification', error);
        }
    };

    const getTypeStyles = (type) => {
        switch (type) {
            case 'success': return 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/40';
            case 'warning': return 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/40';
            case 'error': return 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/40';
            default: return 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/40';
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 dark:hover:text-white rounded-full transition-colors focus:outline-none"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-[#00152E]">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-[#002147] rounded-xl shadow-2xl border border-gray-100 dark:border-[#1a3884]/45 z-50 overflow-hidden transform origin-top-right transition-all animate-in fade-in zoom-in duration-200">
                    <div className="flex items-center justify-between p-4 border-b border-gray-50 dark:border-[#1a3884]/20 bg-gray-50/50 dark:bg-[#001f40]/80">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="text-xs font-semibold text-[#1a3884] dark:text-[#4f7dff] hover:text-[#1a3884]/80 dark:hover:text-[#4f7dff]/80 transition-colors"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="py-12 px-4 text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 dark:bg-[#001f40]/80 mb-3">
                                    <Bell className="text-gray-300 dark:text-gray-400" size={20} />
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-300 font-medium">No alerts yet</p>
                                <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">We'll notify you when something important happens.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-50 dark:divide-[#1a3884]/15">
                                {notifications.map((n) => (
                                    <div
                                        key={n._id}
                                        onClick={() => handleNotificationClick(n)}
                                        className={`relative group p-4 hover:bg-gray-50 dark:hover:bg-[#001f40]/45 transition-all cursor-pointer ${!n.read ? 'bg-blue-50/30 dark:bg-[#1a3884]/20' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase border ${getTypeStyles(n.type)}`}>
                                                        {n.type}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400 dark:text-gray-400 font-medium">
                                                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                                                    </span>
                                                </div>
                                                <h4 className={`text-sm font-semibold ${!n.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                                    {n.title}
                                                </h4>
                                                <p className="text-xs text-gray-500 dark:text-gray-300 mt-1 leading-relaxed">
                                                    {n.message}
                                                </p>
                                                {n.link && (
                                                    <span className="inline-flex items-center mt-2 text-xs font-semibold text-[#1a3884] dark:text-[#4f7dff] hover:text-[#1a3884]/80 dark:hover:text-[#4f7dff]/80">
                                                        View details <ExternalLink size={10} className="ml-1" />
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => handleDelete(e, n._id)}
                                                className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all"
                                                title="Delete Notification"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        {!n.read && (
                                            <span className="absolute top-4 right-10 h-2 w-2 rounded-full bg-[#4f7dff] ring-2 ring-white dark:ring-[#002147]"></span>
                                        )}
                                    </div>
                                ))}
							</div>
                        )}
                    </div>

                    <div className="p-3 bg-gray-50/50 dark:bg-[#001f40]/80 border-t border-gray-50 dark:border-[#1a3884]/20 text-center">
                        <Link
                            to="/notifications"
                            className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            View All Activity
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
